import { type ScaleBand } from 'd3-scale';
import { useRef } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { ChoicesStaticOptions } from '../types';
import { getLongestLabel } from '../utils/get-longest-label';
import { TextFitGhost } from '../components/text-fit-ghost/TextFitGhost';

export const BARS_VISUALIZATION_LABEL_FONTSIZES = [
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '24px',
  '28px',
  '32px',
];

interface BarsAxisLabelsProps {
  staticOptions: ChoicesStaticOptions[];
  labelAccessor: (d: ChoicesStaticOptions) => string;
  keyAccessor: (d: ChoicesStaticOptions) => string;
  discreteScale: ScaleBand<string>;
  totalWidth: number;
  totalHeight: number;
  marginLeft: number;
  marginTop: number;
  color: string;
}

export const BarsAxisLabels = ({
  staticOptions,
  labelAccessor,
  keyAccessor,
  discreteScale,
  totalWidth,
  totalHeight,
  marginLeft,
  marginTop,
  color,
}: BarsAxisLabelsProps) => {
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);

  const longestLabelId = getLongestLabel(
    staticOptions,
    labelAccessor,
    keyAccessor,
  );

  return (
    <Box
      data-testid="bars-axis-labels"
      ref={labelsContainerRef}
      width={totalWidth}
      height={totalHeight}
      position="relative"
      left={marginLeft}
      extend={() => ({ opacity: 0 })}
    >
      {staticOptions.map((d) => {
        const x = discreteScale(keyAccessor(d));
        const label = labelAccessor(d);
        const isLongestLabel = longestLabelId === keyAccessor(d);

        return (
          <Box
            key={d.id}
            width={discreteScale.bandwidth()}
            height={totalHeight - marginTop}
            position="absolute"
            left={x}
            top={marginTop}
            style={{
              wordBreak: 'break-word',
              color,
            }}
          >
            {isLongestLabel && (
              <TextFitGhost
                text={label}
                fontSizes={BARS_VISUALIZATION_LABEL_FONTSIZES}
                width={discreteScale.bandwidth()}
                height={totalHeight - marginTop}
                finalFontSizeContainerRef={labelsContainerRef}
              >
                {(ref) => (
                  <Text ref={ref} aria-hidden="true">
                    {label}
                  </Text>
                )}
              </TextFitGhost>
            )}
            <Text fontSize="inherit">{label}</Text>
          </Box>
        );
      })}
    </Box>
  );
};
